import React from "react";

// Cards that change section glide instead of jumping (founder, build 44: "when
// a song goes from pending to queued it's really jerky... a cool animation
// where it goes from one to the other" — and "these kids send tons of requests
// at once"). FLIP, measured at the only two moments that can't lie:
// getSnapshotBeforeUpdate reads every tagged card's on-screen position just
// BEFORE React touches the DOM, componentDidUpdate reads it again just after.
// Scrolling between updates, the browser's scroll anchoring, or a glide still
// in flight can never fake a move — a new glide starts from wherever the card
// is on screen. When a card turns up in a different section (accepted →
// Queued) every card that moved glides from where it was, and the one that
// changed section lands with a soft green glow (.offer-card--landed,
// OfferCard.css). Cards off screen both before and after aren't animated.
// Visual only: no state, no money path. With prefers-reduced-motion nothing
// glides: the card that changed section fades in where it lands with the same
// green glow (OfferCard.css), while a copy of it fades out where it was
// (founder, 1.5.2) — every other card moves instantly. A browser that can't
// animate does nothing. Renders nothing — it watches the cards inside `root`
// (data-move-key / data-move-list).

type Spot = { top: number; left: number; width: number; height: number; list: string; el: HTMLElement };
type Spots = Map<string, Spot>;

const GLIDE: KeyframeAnimationOptions = { duration: 420, easing: "cubic-bezier(.2,.8,.2,1)" };
const LANDED_MS = 900;
const FADE_OUT: KeyframeAnimationOptions = { duration: 320, easing: "ease-out", fill: "forwards" };
const glides = new WeakMap<HTMLElement, Animation>();

// Reduce Motion: a copy of the card, where it was before it left its section,
// fading out (pointer-events off, hidden from screen readers, removed after).
// Placed inside the cards' own container so it keeps their display-size look.
function fadeOutWhereItWas(root: HTMLElement, was: Spot) {
  const host = root.querySelector<HTMLElement>(".requests-container") || root;
  if (typeof was.el.animate !== "function" || was.height < 1) return;
  const ghost = was.el.cloneNode(true) as HTMLElement;
  ghost.classList.remove("offer-card--landed");
  ghost.removeAttribute("data-move-key");
  ghost.removeAttribute("data-move-list");
  ghost.setAttribute("aria-hidden", "true");
  const box = host.getBoundingClientRect();
  Object.assign(ghost.style, {
    position: "absolute",
    top: `${was.top - box.top + host.scrollTop}px`,
    left: `${was.left - box.left}px`,
    width: `${was.width}px`,
    height: `${was.height}px`,
    boxSizing: "border-box",
    margin: "0",
    zIndex: "2",
    pointerEvents: "none",
    transform: "none",
  });
  host.appendChild(ghost);
  const done = () => ghost.remove();
  try {
    ghost.animate([{ opacity: 1 }, { opacity: 0 }], FADE_OUT).onfinish = done;
  } catch {
    done();
    return;
  }
  window.setTimeout(done, 1000); // belt and braces: never leave a copy behind
}

function measure(root: HTMLElement | null): Spots | null {
  if (!root) return null;
  const spots: Spots = new Map();
  root.querySelectorAll<HTMLElement>("[data-move-key]").forEach((el) => {
    const r = el.getBoundingClientRect();
    spots.set(el.dataset.moveKey!, { top: r.top, left: r.left, width: r.width, height: r.height, list: el.dataset.moveList || "", el });
  });
  return spots;
}

type Props = { root: React.RefObject<HTMLElement> };

export default class CardMoves extends React.Component<Props> {
  getSnapshotBeforeUpdate(): Spots | null {
    return measure(this.props.root.current);
  }

  componentDidUpdate(_props: Props, _state: unknown, before: Spots | null) {
    const after = measure(this.props.root.current);
    if (!before || !after) return;
    const changedSection = Array.from(after).filter(([key, spot]) => before.has(key) && before.get(key)!.list !== spot.list);
    if (changedSection.length === 0) return;
    if (typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      const root = this.props.root.current!;
      changedSection.forEach(([key, { el }]) => {
        fadeOutWhereItWas(root, before.get(key)!);
        el.classList.add("offer-card--landed");
        window.setTimeout(() => el.classList.remove("offer-card--landed"), LANDED_MS);
      });
      return;
    }

    const vh = window.innerHeight;
    const offScreen = (top: number) => top < -vh || top > 2 * vh;
    after.forEach((spot, key) => {
      const was = before.get(key);
      if (!was || typeof spot.el.animate !== "function") return;
      const dy = was.top - spot.top;
      if (Math.abs(dy) < 1 || (offScreen(was.top) && offScreen(spot.top))) return;
      glides.get(spot.el)?.cancel();
      glides.set(spot.el, spot.el.animate([{ transform: `translateY(${dy}px)` }, { transform: "translateY(0)" }], GLIDE));
    });
    changedSection.forEach(([, { el }]) => {
      el.classList.add("offer-card--landed");
      window.setTimeout(() => el.classList.remove("offer-card--landed"), LANDED_MS);
    });
  }

  render() {
    return null;
  }
}
